import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { ROLE_LABELS } from "@/lib/format";
import { api, formatApiError, API_BASE } from "@/lib/api";
import KosyamLogo from "@/components/KosyamLogo";
import SchoolContextSwitcher from "@/components/SchoolContextSwitcher";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import {
  House, Users as UsersIcon, GraduationCap, Chalkboard, ClipboardText,
  CurrencyDollar, Books, UserGear, Gear, SignOut, Sun, MoonStars, Bell, ShieldStar,
  Buildings, Stack, ChalkboardTeacher, Student, Calendar, ChatCircleDots, UserPlus,
  ShieldCheck, Translate, Globe, EnvelopeSimple, Package,
} from "@phosphor-icons/react";

// Every NAV item has:
//   - `labelKey` → nav.<key> in the i18n bundle (full label for the sidebar)
//   - `shortKey` → nav.short.<key> for the mobile bottom bar (falls back to labelKey)
const NAV_ITEMS = [
  { to: "/app", labelKey: "nav.dashboard", shortKey: "nav.short.dashboard", icon: House, module: "finance", end: true },
  { to: "/app/direction", labelKey: "nav.direction", shortKey: "nav.short.direction", icon: Buildings,
    roles: ["super_admin","owner","admin","director","study_director"] },
  { to: "/app/eleve", labelKey: "nav.my_studies", shortKey: "nav.short.my_studies", icon: Student, roles: ["student"] },
  { to: "/app/enseignant", labelKey: "nav.my_courses", shortKey: "nav.short.my_courses", icon: ChalkboardTeacher,
    roles: ["super_admin","owner","admin","director","study_director","teacher"] },
  { to: "/app/eleves", labelKey: "nav.students", shortKey: "nav.short.students", icon: GraduationCap,
    roles: ["super_admin","owner","admin","director","study_director","secretary","teacher","accountant","cashier"] },
  { to: "/app/classes", labelKey: "nav.classes", shortKey: "nav.short.classes", icon: Chalkboard, module: "academic" },
  { to: "/app/structure", labelKey: "nav.academic_structure", shortKey: "nav.short.academic_structure", icon: Stack, module: "academic" },
  { to: "/app/emploi-du-temps", labelKey: "nav.timetable", shortKey: "nav.short.timetable", icon: Calendar, module: "timetable" },
  { to: "/app/agenda", labelKey: "nav.agenda", shortKey: "nav.short.agenda", icon: Calendar,
    roles: ["super_admin","owner","admin","director","study_director","teacher","student","secretary","supervisor"] },
  { to: "/app/messagerie", labelKey: "nav.messaging", shortKey: "nav.short.messaging", icon: ChatCircleDots, module: "messaging" },
  { to: "/app/admissions", labelKey: "nav.admissions", shortKey: "nav.short.admissions", icon: UserPlus, module: "admissions" },
  { to: "/app/notes", labelKey: "nav.grades", shortKey: "nav.short.grades", icon: Books,
    roles: ["super_admin","owner","admin","director","study_director","teacher"] },
  { to: "/app/bulletin", labelKey: "nav.student_bulletin", shortKey: "nav.short.student_bulletin", icon: GraduationCap,
    roles: ["super_admin","owner","admin","director","study_director","teacher","secretary","student"] },
  { to: "/app/presences", labelKey: "nav.attendance", shortKey: "nav.short.attendance", icon: ClipboardText,
    roles: ["super_admin","owner","admin","director","study_director","secretary","teacher","supervisor"] },
  { to: "/app/finances", labelKey: "nav.fees", shortKey: "nav.short.fees", icon: CurrencyDollar, module: "finance" },
  { to: "/app/comptabilite", labelKey: "nav.accounting", shortKey: "nav.short.accounting", icon: UserGear, module: "finance" },
  { to: "/app/utilisateurs", labelKey: "nav.users", shortKey: "nav.short.users", icon: UsersIcon, module: "user_management" },
  { to: "/app/permissions", labelKey: "nav.roles_permissions", shortKey: "nav.short.roles_permissions", icon: ShieldStar, module: "user_management" },
  { to: "/app/admin", labelKey: "nav.admin", shortKey: "nav.short.admin", icon: ShieldStar, module: "admin_panel" },
  { to: "/app/panafricain", labelKey: "nav.panafrican", shortKey: "nav.short.panafrican", icon: Globe,
    roles: ["super_admin"] },
  { to: "/app/notifications", labelKey: "nav.notifications", shortKey: "nav.short.notifications", icon: Bell, roles: null },
  { to: "/app/audit", labelKey: "nav.audit", shortKey: "nav.short.audit", icon: ShieldCheck, module: "audit" },
  { to: "/app/setup", labelKey: "nav.settings", shortKey: "nav.short.settings", icon: Gear,
    roles: ["super_admin","owner","admin","director"] },
  { to: "/app/envois-bulletins", labelKey: "nav.email_logs", shortKey: "nav.short.email_logs", icon: EnvelopeSimple,
    roles: ["super_admin","owner","admin","director","study_director"] },
  { to: "/app/demandes", labelKey: "nav.subscription_requests", shortKey: "nav.short.subscription_requests", icon: Package,
    roles: ["super_admin"] },
];

export default function AppLayout() {
  const { user, school, refresh, logout, hasModule } = useAuth();
  const { theme, toggle } = useTheme();
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const [allSchools, setAllSchools] = useState([]);
  const [switching, setSwitching] = useState(false);
  const toggleLanguage = () => {
    const newLang = i18n.language.startsWith("en") ? "fr" : "en";
    i18n.changeLanguage(newLang);
  };

  useEffect(() => {
    if (user?.role === "super_admin") {
      api.get("/schools").then(r => setAllSchools(r.data)).catch(() => {});
    }
  }, [user?.role]);

  const switchSchool = async (sid) => {
    if (!sid || sid === school?.id) return;
    setSwitching(true);
    try {
      await api.post(`/admin/switch-school/${sid}`);
      await refresh();
      toast.success("Contexte école basculé");
      // Force full reload to reset all page-level caches
      window.location.reload();
    } catch (e) { toast.error(formatApiError(e)); setSwitching(false); }
  };

  const visibleNav = NAV_ITEMS.filter(i => {
    // Prefer module-based check (role + additional permissions). Fallback to roles list.
    if (i.module) return hasModule(i.module);
    if (!i.roles) return true;
    return i.roles.includes(user.role) || user.role === "super_admin";
  });
  const logoUrl = school?.logo_url ? (school.logo_url.startsWith("http") ? school.logo_url : `${API_BASE.replace("/api", "")}${school.logo_url}`) : null;

  return (
    <div className="min-h-screen bg-background flex text-foreground">
      {/* Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl overflow-hidden text-primary flex-shrink-0 flex items-center justify-center">
              {logoUrl ? <img src={logoUrl} alt="" className="w-full h-full object-cover" /> : <KosyamLogo size={56} />}
            </div>
            <div className="min-w-0 flex-1 flex flex-col justify-center min-h-[56px]">
              <div className="font-heading font-bold text-xl leading-tight">{t("app.brand.name")}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest mt-1">{t("app.brand.tagline_short")}</div>
              <div className="text-[9px] text-muted-foreground/70 mt-0.5">
                <a href="https://nathortechnologies.com" target="_blank" rel="noopener noreferrer"
                   className="hover:text-foreground transition-colors" data-testid="link-nathor-sidebar">
                  {t("app.brand.attribution")}
                </a>
              </div>
              {/* Non-super_admin only: display their current school here. For super_admin,
                  the school context is shown by SchoolContextSwitcher below to avoid
                  a stale/duplicate label mismatching the active context. */}
              {user.role !== "super_admin" && (
                <div className="text-xs text-muted-foreground mt-1 truncate" data-testid="sidebar-school-name">
                  {school?.name || (i18n.language.startsWith("en") ? "Setup required" : "Configuration requise")}
                </div>
              )}
            </div>
          </div>
          {user.role === "super_admin" && (
            <div className="mt-4">
              <SchoolContextSwitcher />
              <p className="text-[10px] text-muted-foreground mt-1.5 leading-tight">
                Toutes les pages sont scopées à l'école sélectionnée. « Toutes les écoles » = vue panafricaine globale.
              </p>
            </div>
          )}
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {visibleNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`nav-${item.to.split("/").pop() || "home"}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted"
                }`
              }
            >
              <item.icon size={18} weight="duotone" />
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-border flex items-center justify-between">
          <div className="text-xs text-muted-foreground truncate max-w-[130px]">
            <div className="font-medium text-foreground truncate">{user.name}</div>
            <div className="truncate">{ROLE_LABELS[user.role] || user.role}</div>
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost" size="icon" onClick={toggleLanguage}
              data-testid="btn-lang-toggle"
              aria-label={t("language.label")}
              title={i18n.language.startsWith("en") ? "Passer en français" : "Switch to English"}
            >
              <span className="flex items-center gap-0.5 text-[10px] font-mono font-bold">
                <Translate size={14} weight="duotone" />
                {i18n.language.startsWith("en") ? "EN" : "FR"}
              </span>
            </Button>
            <Button
              variant="ghost" size="icon" onClick={toggle}
              data-testid="btn-theme-toggle" aria-label="Changer thème"
            >
              {theme === "dark" ? <Sun size={18} /> : <MoonStars size={18} />}
            </Button>
            <Button
              variant="ghost" size="icon"
              onClick={async () => { await logout(); nav("/login"); }}
              data-testid="btn-logout" aria-label="Déconnexion"
            >
              <SignOut size={18} />
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar — adaptive : super_admin en compact (partage avec switcher),
          autres rôles en aéré (pas de switcher = plus d'espace pour le slogan). */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-30 bg-card/95 backdrop-blur border-b border-border flex items-center gap-2 px-2 h-14">
        {user?.role === "super_admin" ? (
          <>
            <div className="flex items-center gap-1.5 shrink-0">
              <KosyamLogo size={26} className="text-primary shrink-0" />
              <div className="flex flex-col justify-center leading-[1.05] w-[86px]">
                <div className="font-heading font-bold text-[12px]">{t("app.brand.name")}</div>
                <div className="text-[7px] text-muted-foreground uppercase tracking-tight leading-[1.15] line-clamp-2" data-testid="mobile-brand-tagline">
                  {t("app.brand.tagline_short")}
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-0" data-testid="mobile-school-context-wrap">
              <SchoolContextSwitcher />
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <KosyamLogo size={34} className="text-primary shrink-0" />
            <div className="flex flex-col justify-center leading-tight min-w-0">
              <div className="font-heading font-bold text-sm">{t("app.brand.name")}</div>
              <div className="text-[9px] text-muted-foreground uppercase tracking-wider truncate" data-testid="mobile-brand-tagline">
                {t("app.brand.tagline_short")}
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center gap-0 shrink-0">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleLanguage} data-testid="btn-lang-toggle-mobile" aria-label="Language">
            <span className="text-[10px] font-mono font-bold">{i18n.language.startsWith("en") ? "EN" : "FR"}</span>
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggle} data-testid="btn-theme-toggle-mobile">
            {theme === "dark" ? <Sun size={16} /> : <MoonStars size={16} />}
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={async () => { await logout(); nav("/login"); }} data-testid="btn-logout-mobile">
            <SignOut size={16} />
          </Button>
        </div>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border flex overflow-x-auto no-scrollbar">
        {visibleNav.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end}
            data-testid={`nav-mobile-${item.to.split("/").pop() || "home"}`}
            className={({ isActive }) =>
              `min-w-[70px] flex flex-col items-center gap-1 px-2 py-2 text-[10px] ${
                isActive ? "text-accent" : "text-muted-foreground"
              }`
            }
          >
            <item.icon size={20} weight={"duotone"} />
            {t(item.shortKey, { defaultValue: t(item.labelKey).split(" ")[0] })}
          </NavLink>
        ))}
      </nav>

      <main className="flex-1 min-w-0 pb-24 lg:pb-0 pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
